"use client";
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { useRef, useEffect } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { autoPlacement, offset } from '@floating-ui/react';
import { IconCalendarUI } from '../icons/ui_general/IconCalendarUI.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import fi from 'date-fns/locale/fi';
import sv from 'date-fns/locale/sv';
import enUS from 'date-fns/locale/en-US';
import enGB from 'date-fns/locale/en-GB';
import { inPixels, px2rem } from '../utils/helpers/style.js';
import { InputField } from '../InputField/InputField.web.js';

var _excluded = ["dateFormat", "error", "errorMessage", "excludeDates", "filterDate", "includeDates", "label", "locale", "disabled", "maxDate", "minDate", "name", "placeholder", "color", "borderStyle", "mode", "size"];
registerLocale('fi', fi);
registerLocale('sv', sv);
registerLocale('en-GB', enGB);
registerLocale('en', enUS);
var InputFieldMock = function InputFieldMock(props, ref) {
  return /*#__PURE__*/React.createElement(InputField, _extends({}, props, {
    inputRef: ref
  }));
};
var InputFieldWithRef = /*#__PURE__*/React.forwardRef(InputFieldMock);
function getDatePickerPropsFromMode(mode) {
  switch (mode) {
    case 'year':
      return {
        showYearPicker: true
      };
    case 'week':
      return {
        showWeekPicker: true,
        showWeekNumbers: true
      };
    case 'month':
      return {
        showMonthYearPicker: true,
        showFullMonthYearPicker: true
      };
    default:
      return {};
  }
}
var DatePickerLazy = function DatePickerLazy(_ref) {
  var dateFormat = _ref.dateFormat,
    error = _ref.error,
    errorMessage = _ref.errorMessage,
    excludeDates = _ref.excludeDates,
    filterDate = _ref.filterDate,
    includeDates = _ref.includeDates,
    label = _ref.label,
    _ref$locale = _ref.locale,
    locale = _ref$locale === undefined ? 'fi' : _ref$locale,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    maxDate = _ref.maxDate,
    minDate = _ref.minDate,
    name = _ref.name,
    placeholder = _ref.placeholder,
    color = _ref.color,
    borderStyle = _ref.borderStyle,
    _ref$mode = _ref.mode,
    mode = _ref$mode === undefined ? 'date' : _ref$mode,
    size = _ref.size,
    props = _objectWithoutProperties(_ref, _excluded);
  var ariaLabels = useTheme().datepicker.web.ariaLabels;
  var modeProps = getDatePickerPropsFromMode(mode);
  var ref = useRef(null);

  // Workaround for escaping range selection on keyboard navigation where initial start date is not set for months mode.
  useEffect(function () {
    if (ref.current && props.selectsRange) {
      if (props.startDate) {
        // istanbul ignore next - Does not work in test environment
        if (ref.current.calendar) {
          ref.current.calendar.handleMonthChange(props.startDate);
        }
      }
    }
  }, [props.startDate, props.selectsRange]);
  return /*#__PURE__*/React.createElement(ReactDatePicker, _extends({
    ref: ref,
    "data-testid": "elemental-DatePicker_react",
    customInput: /*#__PURE__*/React.createElement(InputFieldWithRef, {
      errorMessage: errorMessage,
      error: error,
      color: color,
      label: label,
      size: size,
      name: name,
      borderStyle: borderStyle,
      placeholder: placeholder,
      disabled: disabled,
      rightIcon: /*#__PURE__*/React.createElement(IconCalendarUI, {
        size: size === 'l' ? inPixels(32) : undefined,
        marginTop: size === 'l' ? px2rem(-4) : undefined
      })
    }),
    dateFormat: dateFormat,
    excludeDates: excludeDates,
    filterDate: filterDate,
    fixedHeight: true,
    includeDates: includeDates,
    locale: locale,
    maxDate: maxDate,
    minDate: minDate,
    name: name,
    placeholderText: placeholder,
    chooseDayAriaLabelPrefix: ariaLabels.chooseDayAriaLabelPrefix[locale],
    disabledDayAriaLabelPrefix: ariaLabels.disabledDayAriaLabelPrefix[locale],
    weekAriaLabelPrefix: ariaLabels.weekAriaLabelPrefix[locale],
    nextMonthAriaLabel: ariaLabels.nextMonthAriaLabel[locale],
    previousMonthAriaLabel: ariaLabels.previousMonthAriaLabel[locale],
    nextYearAriaLabel: ariaLabels.nextYearAriaLabel[locale],
    previousYearAriaLabel: ariaLabels.previousYearAriaLabel[locale],
    disabled: disabled,
    popperModifiers: [autoPlacement({
      allowedPlacements: ['bottom']
    }), offset(-2)],
    popperPlacement: "bottom"
  }, modeProps, props));
};

export { DatePickerLazy, DatePickerLazy as default };
